import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import {
  LuWallet,
  LuHeadphones,
  LuShieldCheck,
  LuSparkles,
  LuClock,
  LuCircleCheck,
  LuChartLine,
  LuPlay,
  LuRadio,
  LuChevronDown,
  LuSmartphone,
  LuDownload,
  LuListChecks,
  LuStar,
  LuCrown,
} from "react-icons/lu";
import { FaAndroid } from "react-icons/fa";
import Seo from "../components/Seo";
import DownloadButton from "../components/DownloadButton";
import { useJackpot, useMarkets, useNow, useRates, useResults, useStarline } from "../hooks/useData";
import { APK_URL, BRAND, siteUrl } from "../utils/config";
import { latestDay, resultString, slotStatus } from "../utils/data";
import { trackDownload } from "../utils/analytics";

const FEATURES = [
  { icon: LuWallet, title: "Fast Withdrawal", text: "Quick payouts to your account" },
  { icon: LuHeadphones, title: "24/7 Support", text: "Help via Call & WhatsApp" },
  { icon: LuShieldCheck, title: "100% Secure", text: "Encrypted, safe transactions" },
  { icon: LuSparkles, title: "Premium Service", text: "A smooth playing experience" },
];

const STEPS = [
  { icon: LuDownload, title: "Download the app", text: "Tap Download App and install the Ratan Market APK on your Android phone." },
  { icon: LuSmartphone, title: "Register & add points", text: "Create your account and add points to your wallet from the app." },
  { icon: LuListChecks, title: "Pick a market & play", text: "Choose an open market, select your game and numbers, then confirm." },
];

export const FAQS = [
  {
    q: "Where can I see the satta matka result today?",
    a: "The Latest Matka Results strip and the Best Games cards on this page show the newest declared results, and the Matka Result Today page lists every market in one table with its open pana, jodi and close pana.",
  },
  {
    q: "What is the Kalyan matka chart?",
    a: "The Kalyan matka chart is the history of the Kalyan market. Open the Kalyan Jodi chart for the two-digit jodi of each day, or the Kalyan Pana chart for the open pana, jodi and close pana.",
  },
  {
    q: "What is Ratan Market?",
    a: "Ratan Market is a matka results and charts website. It publishes market timings, live results and week-by-week Jodi and Panel (Pana) charts for markets such as Kalyan, Milan, Madhur, Sridevi, Main Bazar, Rajdhani, Time Bazar, Supreme and Ratan Khatri.",
  },
  {
    q: "How do I check the matka result today?",
    a: "Open the home page and look at the Best Games section: every market shows its open pana, jodi and close pana as soon as they are declared. Markets that have not declared yet show XXX - XX - XXX.",
  },
  {
    q: "What is a Jodi chart?",
    a: "A Jodi chart lists the two-digit jodi of a market for every day, grouped week by week from Monday to Sunday. The jodi is the last digit of the open pana sum followed by the last digit of the close pana sum.",
  },
  {
    q: "What is a Pana (Panel) chart?",
    a: "A Pana chart shows the full result of each day — open pana on top, the jodi in the middle and the close pana below — so you can study the complete panel history of a market.",
  },
  {
    q: "What do XXX and ** mean in the results?",
    a: "XXX, XX, ** and *** mean that part of the result has not been declared yet. They are replaced automatically as soon as the market declares.",
  },
  {
    q: "Which matka markets are covered?",
    a: "All active markets are listed on the All Charts page, including Kalyan, Kalyan Morning, Kalyan Night, Karnataka Day, Madhur Day/Morning/Night, Main Bazar, Milan Day/Morning/Night, Rajdhani Day/Night, Ratan Khatri, Sridevi, Sridevi Morning/Night, Supreme Day/Night and Time Bazar.",
  },
  {
    q: "Is it free to view results and charts?",
    a: "Yes. Viewing results, timings and every Jodi and Pana chart on this website is free and needs no registration.",
  },
  {
    q: "Who can play?",
    a: "Only people aged 18 or above, and only where the law allows it. Gambling is restricted or prohibited in many places, so please check the rules where you live and play responsibly.",
  },
];

const SlotSection = ({ id, title, icon: Icon, sub, basePath, slots, bySlug, today, now, text, placeholder }) => (
  <section className="rt-section rt-section--tint" id={id} aria-labelledby={`${id}-h`}>
    <div className="rt-wrap">
      <h2 className="rt-title" id={`${id}-h`}>
        <Icon size={22} aria-hidden="true" /> {title}
      </h2>
      <div className="rt-line" />
      <p className="rt-sub">{sub}</p>
      <div className="rt-slots">
        {slots.map((s) => {
          const d = (bySlug[s.slug] || {})[today];
          const st = slotStatus(s.name, now);
          return (
            <Link key={s.slug} to={`${basePath}/${s.slug}`} className="rt-slot" title={`${s.name} chart`}>
              <span className="rt-slot__time">{s.name}</span>
              <strong className={d ? "is-live" : ""}>{text(d) || placeholder}</strong>
              <span className={`rt-badge ${st.open ? "rt-badge--open" : "rt-badge--closed"}`}>{st.text}</span>
            </Link>
          );
        })}
      </div>
      <p className="rt-slots__more">
        <Link to={basePath}>See all {title} →</Link>
      </p>
    </div>
  </section>
);

const OpenBadge = ({ open }) => (
  <span className={`rt-badge ${open ? "rt-badge--open" : "rt-badge--closed"}`}>{open ? "Open" : "Closed"}</span>
);

const Home = () => {
  const { markets, loading } = useMarkets();
  const { bySlug } = useResults();
  const rates = useRates();
  const starline = useStarline();
  const jackpot = useJackpot();
  const now = useNow();
  const [faq, setFaq] = useState(0);
  const today = now.format("YYYY-MM-DD");

  const cards = useMemo(
    () =>
      markets.map((m) => {
        const days = bySlug[m.slug] || {};
        return { ...m, today: days[today] || null, last: latestDay(days) };
      }),
    [markets, bySlug, today]
  );

  const ticker = useMemo(
    () =>
      cards
        .filter((c) => c.last)
        .sort((a, b) => b.last.date.localeCompare(a.last.date))
        .slice(0, 14),
    [cards]
  );

  const origin = siteUrl();
  const schema = useMemo(
    () => [
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: FAQS.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
      {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: "Matka charts",
        itemListElement: markets.flatMap((m, i) => [
          { "@type": "ListItem", position: i * 2 + 1, name: `${m.name} Jodi Chart`, url: `${origin}/jodi/${m.slug}` },
          { "@type": "ListItem", position: i * 2 + 2, name: `${m.name} Pana Chart`, url: `${origin}/pana/${m.slug}` },
        ]),
      },
      {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        name: `${BRAND.name} App`,
        operatingSystem: "Android",
        applicationCategory: "GameApplication",
        downloadUrl: APK_URL,
        offers: { "@type": "Offer", price: "0", priceCurrency: "INR" },
      },
    ],
    [markets, origin]
  );

  return (
    <main>
      <Seo path="/" schema={schema} />

      {/* HERO */}
      <section className="rt-hero">
        <div className="rt-rain" aria-hidden="true">
          {["♦", "♠", "J", "Q", "10", "♥", "A", "♦", "9", "K", "♣"].map((c, i) => (
            <span key={i} className={`rt-rain__card${"♦♥".includes(c) ? " is-red" : ""}`}>
              {c}
            </span>
          ))}
        </div>
        <div className="rt-wrap rt-hero__grid">
          <div className="rt-hero__copy">
            <span className="rt-eyebrow">
              <LuRadio size={14} aria-hidden="true" /> Play Matka Online
            </span>
            <h1>
              Satta Matka Result Today &amp; Live Jodi, Panel Charts on <span>{BRAND.name}</span>
            </h1>
            <p>
              India&apos;s trusted place to follow satta matka results. Live matka results, daily Jodi and Panel charts,
              market timings and fast, safe withdrawals — all on your phone.
            </p>
            <div className="rt-hero__cta" data-hero-cta>
              <DownloadButton />
              <Link to="/matka-result-today" className="rt-btn-ghost">
                <LuChartLine size={18} aria-hidden="true" /> Matka Result Today
              </Link>
            </div>
            <ul className="rt-chips">
              <li>
                <LuCircleCheck size={15} aria-hidden="true" /> Free results &amp; charts
              </li>
              <li>
                <LuCircleCheck size={15} aria-hidden="true" /> No registration to view
              </li>
              <li>
                <LuCircleCheck size={15} aria-hidden="true" /> 18+ only
              </li>
            </ul>
          </div>
          <div className="rt-hero__art">
            <img
              src="/hero-art.svg"
              alt="Matka pot with playing cards, coins and result numbers"
              width="480"
              height="360"
              fetchpriority="high"
            />
          </div>
        </div>
      </section>

      <p className="rt-lead">
        {BRAND.name} is a simple, mobile-friendly matka results platform. We publish live matka charts, daily Jodi and
        Panel summaries and real-time market updates — right on your phone.
      </p>

      {/* FEATURES */}
      <section className="rt-wrap rt-features" aria-label="Why play with us">
        {FEATURES.map((f) => (
          <article key={f.title} className="rt-feature">
            <span className="rt-feature__ic">
              <f.icon size={26} aria-hidden="true" />
            </span>
            <h2>{f.title}</h2>
            <p>{f.text}</p>
          </article>
        ))}
      </section>

      {/* LATEST RESULTS */}
      <section className="rt-section" aria-labelledby="latest-h">
        <div className="rt-wrap">
          <h2 className="rt-title" id="latest-h">
            <LuRadio size={22} aria-hidden="true" /> Latest Matka Results
          </h2>
          <div className="rt-line" />
          <p className="rt-sub">The most recently declared result of every market.</p>
        </div>
        {ticker.length > 0 ? (
          <div className="rt-marquee">
            <div className="rt-marquee__track">
              {[...ticker, ...ticker].map((c, i) => (
                <Link
                  key={`${c.slug}-${i}`}
                  to={`/pana/${c.slug}`}
                  className="rt-tick"
                  aria-hidden={i >= ticker.length}
                  tabIndex={i >= ticker.length ? -1 : 0}
                >
                  <span className="rt-tick__name">{c.name}</span>
                  <strong>{resultString(c.last)}</strong>
                  <small>{moment(c.last.date).format("DD MMM YYYY")}</small>
                </Link>
              ))}
            </div>
          </div>
        ) : (
          <p className="rt-empty">{loading ? "Loading results…" : "Results will appear here as soon as they are declared."}</p>
        )}
      </section>

      {/* RATES */}
      <section className="rt-section rt-section--tint" aria-labelledby="rates-h">
        <div className="rt-wrap">
          <h2 className="rt-title" id="rates-h">
            Best Game Rates
          </h2>
          <div className="rt-line" />
          <p className="rt-sub">Highest payout rates available for all major games.</p>
          <div className="rt-rates">
            {rates.map(([label, value]) => (
              <div key={label} className="rt-rates__row">
                <span>{label}</span>
                <strong>{value}</strong>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GAMES */}
      <section className="rt-section" id="games" aria-labelledby="games-h">
        <div className="rt-wrap">
          <h2 className="rt-title" id="games-h">
            Best Games
          </h2>
          <div className="rt-line" />
          <p className="rt-sub">Results from all matka markets. Updated automatically.</p>
          <div className="rt-grid">
            {cards.map((c) => (
              <article key={c.slug} className="rt-game">
                <header>
                  <h3>
                    {c.name} <LuCircleCheck size={15} aria-hidden="true" />
                  </h3>
                  <OpenBadge open={c.isOpen} />
                </header>
                <p className="rt-game__time">
                  <LuClock size={13} aria-hidden="true" /> {c.openTime} - {c.closeTime}
                </p>
                <div className={`rt-game__result${c.today ? " is-live" : ""}`}>{resultString(c.today)}</div>
                {c.last && (
                  <p className="rt-game__last">
                    Last: <b>{resultString(c.last)}</b> · {moment(c.last.date).format("DD MMM")}
                  </p>
                )}
                <div className="rt-game__btns">
                  <a href={APK_URL} download rel="nofollow noopener" onClick={trackDownload} className="rt-play">
                    <LuPlay size={14} aria-hidden="true" /> Play
                  </a>
                  <Link to={`/jodi/${c.slug}`} className="rt-mini" title={`${c.name} Jodi chart`}>
                    Jodi
                  </Link>
                  <Link to={`/pana/${c.slug}`} className="rt-mini" title={`${c.name} Pana chart`}>
                    Pana
                  </Link>
                </div>
              </article>
            ))}
          </div>
          {!loading && cards.length === 0 && <p className="rt-empty">Markets are not available right now. Please try again shortly.</p>}
        </div>
      </section>

      <SlotSection
        id="starline"
        title="King Starline"
        icon={LuStar}
        sub="A new result every hour. Tap a slot to open its chart."
        basePath="/starline"
        slots={starline.slots}
        bySlug={starline.bySlug}
        today={today}
        now={now}
        placeholder="XXX - X"
        text={(d) => (d ? `${d.panna} - ${d.digit}` : null)}
      />
      <SlotSection
        id="jackpot"
        title="King Jackpot"
        icon={LuCrown}
        sub="Hourly jackpot results. Tap a slot to open its chart."
        basePath="/jackpot"
        slots={jackpot.slots}
        bySlug={jackpot.bySlug}
        today={today}
        now={now}
        placeholder="XX"
        text={(d) => (d ? d.value : null)}
      />

      {/* HOW IT WORKS */}
      <section className="rt-section rt-section--tint" aria-labelledby="how-h">
        <div className="rt-wrap">
          <h2 className="rt-title" id="how-h">
            How to Start in 3 Steps
          </h2>
          <div className="rt-line" />
          <ol className="rt-steps">
            {STEPS.map((s, i) => (
              <li key={s.title}>
                <span className="rt-steps__n">{i + 1}</span>
                <s.icon size={26} aria-hidden="true" />
                <h3>{s.title}</h3>
                <p>{s.text}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* APP BANNER */}
      <section className="rt-wrap">
        <div className="rt-appbanner">
          <div>
            <h2>
              Download {BRAND.name} App <small>For Android devices</small>
            </h2>
            <p>
              Get instant access to all matka markets, live results, Jodi and Pana charts and notifications on your
              phone.
            </p>
            <a href={APK_URL} download rel="nofollow noopener" onClick={trackDownload} className="rt-appbanner__btn">
              <FaAndroid size={20} aria-hidden="true" /> Download APK
            </a>
          </div>
        </div>
      </section>

      {/* SEO COPY */}
      <section className="rt-section" aria-labelledby="about-h">
        <div className="rt-wrap rt-prose">
          <h2 id="about-h">Satta Matka Result Today — Live Matka Charts on {BRAND.name}</h2>
          <p>
            {BRAND.name} brings every popular market of <strong>satta matka</strong> to one fast page. Check the{" "}
            <Link to="/matka-result-today">
              <strong>matka result today</strong>
            </Link>{" "}
            for Kalyan, Milan, Madhur, Sridevi, Main Bazar, Rajdhani, Time Bazar, Supreme and Ratan Khatri, see the exact
            open and close timings, and open the full history of any market in one tap.
          </p>

          <h3>What is satta matka?</h3>
          <p>
            Satta matka is a number game that began in Mumbai in the 1960s. Every market declares an open pana, a jodi
            and a close pana each day, and players follow these results and the long-term charts. New to the game? Read
            our simple <Link to="/satta-matka">satta matka guide</Link> to learn how a result is made and how to read it.
          </p>

          <h3>Kalyan Matka — Panel Chart &amp; Jodi Chart</h3>
          <p>
            <strong>Kalyan matka</strong> is one of the most followed markets. Each market has its own{" "}
            <Link to="/jodi/kalyan">Kalyan Jodi chart</Link> and <Link to="/pana/kalyan">Kalyan Panel (Pana) chart</Link>.
            Rows are weeks, columns are Monday to Sunday, and the newest week is always on top, so the latest result is
            the first thing you see. You can also open <Link to="/jodi/kalyan-night">Kalyan Night</Link> and{" "}
            <Link to="/jodi/kalyan-morning">Kalyan Morning</Link>.
          </p>

          <h3>Main Bazar, Milan &amp; Madhur Charts</h3>
          <p>
            Follow the <Link to="/jodi/main-bazar">Main Bazar chart</Link>, <Link to="/jodi/milan-day">Milan Day</Link>{" "}
            and <Link to="/jodi/milan-night">Milan Night</Link> results, or compare Madhur Day, Morning and Night in
            seconds. Every chart can be searched by jodi or pana number.
          </p>

          <h3>How to Read a Matka Chart</h3>
          <p>
            A pana is a three-digit number. The jodi is made from the last digit of the open pana sum and the last digit
            of the close pana sum. Red jodi marks doubles such as 11, 22 or 77, and XX or ** simply means that part of the
            result has not been declared yet.
          </p>

          <h3>Why use {BRAND.name}?</h3>
          <ul className="rt-prose__list">
            <li>Every market in one place, with open and close timings.</li>
            <li>Week-by-week Jodi and Pana charts that work smoothly on any phone.</li>
            <li>Fast, searchable charts and a clean design with no clutter.</li>
            <li>Free to view — no registration needed.</li>
          </ul>

          <p className="rt-prose__links">
            <Link to="/matka-result-today">Matka result today</Link>
            <Link to="/charts">All matka charts</Link>
            <Link to="/satta-matka">Satta matka guide</Link>
            <Link to="/how-to-play">How to play</Link>
            <Link to="/about">About {BRAND.name}</Link>
            <Link to="/terms">Terms</Link>
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="rt-section rt-section--tint" aria-labelledby="faq-h">
        <div className="rt-wrap rt-faq">
          <h2 className="rt-title" id="faq-h">
            Frequently Asked Questions
          </h2>
          <div className="rt-line" />
          {FAQS.map((f, i) => (
            <div key={f.q} className={`rt-faq__item${faq === i ? " is-open" : ""}`}>
              <h3>
                <button type="button" aria-expanded={faq === i} onClick={() => setFaq(faq === i ? -1 : i)}>
                  <span>{f.q}</span>
                  <LuChevronDown size={20} aria-hidden="true" />
                </button>
              </h3>
              <p hidden={faq !== i}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>
    </main>
  );
};

export default Home;
